/**
 * PromoManager — Sprint 14-S4
 * Admin/owner page: create promo codes, list active promos, deactivate.
 * Roles: admin, owner only (enforced in App.js ProtectedRoute + Sidebar filter).
 */

import React, { useState, useEffect } from 'react';
import { createPromo, getPromos, deactivatePromo, deletePromo } from '../utils/api';

function formatValue(promo) {
  return promo.type === 'percent' ? `${promo.value}%` : `₹${(promo.value / 100).toLocaleString('en-IN')}`;
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return 'Never';
  return new Date(expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatUsage(promo) {
  const limit = promo.usageLimit === 0 ? '∞' : promo.usageLimit;
  return `${promo.usedCount} / ${limit}`;
}

export default function PromoManager() {
  const [promos, setPromos]           = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError]     = useState('');

  // Create form state
  const [code,       setCode]       = useState('');
  const [type,       setType]       = useState('percent');
  const [value,      setValue]      = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [expiresAt,  setExpiresAt]  = useState('');
  const [creating,   setCreating]   = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Deactivate loading state — tracks per-promo spinner
  const [deactivating, setDeactivating] = useState(null);

  const [deletingId,   setDeletingId]   = useState(null);
  const [deleteErrors, setDeleteErrors] = useState({});

  useEffect(() => {
    fetchPromos();
  }, []);

  async function fetchPromos() {
    setLoadingList(true);
    setListError('');
    try {
      const res = await getPromos();
      setPromos(res.data.data || []);
    } catch {
      setListError('Could not load promo codes.');
    } finally {
      setLoadingList(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    if (!code.trim() || !value) {
      setCreateError('Code and value are required.');
      return;
    }
    setCreating(true);
    try {
      await createPromo({
        code: code.trim().toUpperCase(),
        type,
        value: type === 'fixed' ? Math.round(parseFloat(value) * 100) : parseFloat(value),
        usageLimit: usageLimit ? parseInt(usageLimit, 10) : 0,
        expiresAt:  expiresAt || undefined
      });
      setCreateSuccess(`Promo "${code.trim().toUpperCase()}" created.`);
      setCode(''); setValue(''); setUsageLimit(''); setExpiresAt('');
      await fetchPromos();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Could not create promo code.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this promo code? This cannot be undone.')) return;
    setDeletingId(id);
    setDeleteErrors(prev => { const e = { ...prev }; delete e[id]; return e; });
    try {
      await deletePromo(id);
      setPromos(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not delete promo code.';
      setDeleteErrors(prev => ({ ...prev, [id]: msg }));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDeactivate(id) {
    setDeactivating(id);
    try {
      await deactivatePromo(id);
      setPromos(prev => prev.map(p => p._id === id ? { ...p, isActive: false } : p));
    } catch {
      alert('Could not deactivate promo. Please try again.');
    } finally {
      setDeactivating(null);
    }
  }

  const inputStyle = {
    border: '1px solid #ddd', borderRadius: '6px', padding: '8px 12px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box'
  };
  const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#555',
                        display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', fontFamily: "'Open Sans', sans-serif" }}>
      <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', marginBottom: '24px' }}>Promo Codes</h2>

      {/* Create form */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#333', marginBottom: '20px', marginTop: 0 }}>Create Promo Code</h3>
        <form onSubmit={handleCreate}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Code</label>
              <input
                style={inputStyle}
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. SAVE20"
                maxLength={20}
              />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={type} onChange={e => setType(e.target.value)}>
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{type === 'percent' ? 'Discount (%)' : 'Discount (₹)'}</label>
              <input
                style={inputStyle}
                type="number"
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder={type === 'percent' ? '0–100' : 'Amount in ₹'}
                min="1"
                max={type === 'percent' ? 100 : undefined}
              />
            </div>
            <div>
              <label style={labelStyle}>Usage Limit</label>
              <input
                style={inputStyle}
                type="number"
                value={usageLimit}
                onChange={e => setUsageLimit(e.target.value)}
                placeholder="0 = unlimited"
                min="0"
              />
            </div>
            <div>
              <label style={labelStyle}>Expires (optional)</label>
              <input
                style={inputStyle}
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
              />
            </div>
          </div>

          {createError   && <p style={{ color: '#e74c3c', fontSize: '13px', margin: '8px 0' }}>{createError}</p>}
          {createSuccess && <p style={{ color: '#27ae60', fontSize: '13px', margin: '8px 0' }}>{createSuccess}</p>}

          <button
            type="submit"
            disabled={creating}
            style={{
              background: creating ? '#aaa' : '#2A9D8F', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '10px 24px', fontSize: '14px',
              fontWeight: '700', cursor: creating ? 'not-allowed' : 'pointer'
            }}
          >
            {creating ? 'Creating...' : 'Create Promo'}
          </button>
        </form>
      </div>

      {/* Promo list */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#333', margin: 0 }}>All Promo Codes</h3>
        </div>

        {loadingList ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading...</div>
        ) : listError ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#e74c3c' }}>{listError}</div>
        ) : promos.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>No promo codes yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Code', 'Discount', 'Usage', 'Expires', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '700',
                                       color: '#555', fontSize: '11px', textTransform: 'uppercase',
                                       letterSpacing: '0.5px', borderBottom: '1px solid #e8e8e8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {promos.map(promo => (
                <React.Fragment key={promo._id}>
                  <tr style={{ borderBottom: deleteErrors[promo._id] ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'monospace', fontSize: '14px' }}>
                      {promo.code}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#27ae60', fontWeight: '600' }}>
                      {formatValue(promo)} off
                    </td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{formatUsage(promo)}</td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{formatExpiry(promo.expiresAt)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                        background: promo.isActive ? '#e8f8f3' : '#f8f8f8',
                        color:      promo.isActive ? '#27ae60' : '#999'
                      }}>
                        {promo.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {promo.isActive && (
                          <button
                            onClick={() => handleDeactivate(promo._id)}
                            disabled={deactivating === promo._id || deletingId === promo._id}
                            style={{
                              background: 'none', border: '1px solid #e74c3c', color: '#e74c3c',
                              borderRadius: '6px', padding: '4px 12px', fontSize: '12px',
                              fontWeight: '600',
                              cursor: (deactivating === promo._id || deletingId === promo._id) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {deactivating === promo._id ? '...' : 'Deactivate'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(promo._id)}
                          disabled={deletingId === promo._id || deactivating === promo._id}
                          style={{
                            background: 'none', border: '1px solid #e74c3c', color: '#e74c3c',
                            borderRadius: '6px', padding: '4px 12px', fontSize: '12px',
                            fontWeight: '600',
                            opacity: (deletingId === promo._id || deactivating === promo._id) ? 0.5 : 1,
                            cursor: (deletingId === promo._id || deactivating === promo._id) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {deletingId === promo._id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {deleteErrors[promo._id] && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td colSpan={6} style={{ padding: '4px 16px 10px', color: '#e74c3c', fontSize: '12px' }}>
                        {deleteErrors[promo._id]}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
